















import { computed, defineComponent, ref } from "@vue/composition-api";
import { FileData, InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import { QuestionaryInfo } from "@scrile/api-provider/dist/api/QuestionaryInfoProvider";
import { TagInfo, TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { InputUserUpdate, User } from "@scrile/api-provider/dist/api/UserProvider";
import { InputUserPublicInformationUpdate } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import useTags from "@/hooks/useTags";
import useUserTags from "@/hooks/useUserTags";
import Store from "@/store";
import { MUTATION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormValues } from "@/types";
import PageProfileEditView from "./components/PageProfileEditView.vue";

export default defineComponent({
  name: "PageProfileEdit",
  components: { PageProfileEditView },
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { root }) {
    const fetch = ref(true);
    const loading = ref(false);
    const info = ref<Record<string, string>>({});
    const files = ref<FileData[]>([]);
    const user = ref<UserPublicData | User | null>(null);
    const userTags = ref<string[]>([]);
    const fileLoading = ref(false);

    const { authUserStrict } = useAuthUser();
    const { findFiles, uploadThenUpdate } = useFiles();
    const { userTagsGetData } = useUserTags();
    const { getTags, tags: systemTags } = useTags();
    const isAuthor = computed(() => authUserStrict.value.id === props.userId);

    const selectedTags = computed<TagInfo[]>(() => systemTags.value.filter(t => userTags.value.includes(t.id)));

    const getFiles = async () => {
      files.value = await findFiles({
        fileIds: [
          {
            subjectId: props.userId,
            type: "avatar",
          },
          {
            subjectId: props.userId,
            type: "background_image",
          },
        ],
      });
    };

    const getUserData = async () => {
      if (isAuthor.value) {
        user.value = authUserStrict.value;
      } else {
        const r = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
        user.value = r.result.find(i => i.id === props.userId) ?? null;
      }
    };

    const getUserTags = async () => {
      if (!config.featureTags) return;
      userTags.value = await userTagsGetData();
    };

    const getSystemTags = async () => {
      if (!config.featureTags) return;
      await getTags(TagInfoType.USERS);
    };

    const getData = async () => {
      try {
        fetch.value = true;
        await Promise.all([
          providers.QuestionaryInfoProvider.find({ id: props.userId }).then(
            r => (info.value = r.fields as Record<string, string>)
          ),
          getFiles(),
          getUserData(),
          getSystemTags(),
          getUserTags(),
        ]);
      } finally {
        fetch.value = false;
      }
    };

    const updateUserTags = (tags: string[]) => {
      userTags.value = tags;
    };

    const upload = async (file: FileList, type: string, cb?: () => void) => {
      try {
        fileLoading.value = true;
        if (file.length === 0) return;
        const fileId: InputFileId = {
          type,
          subjectId: props.userId,
        };
        const r = await uploadThenUpdate({ fileId, file: file[0] });
        await getFiles();
        if (user.value) {
          const updateUser = {
            ...user.value,
            ...(r.fileId.type === "avatar" ? { avatar: r } : {}),
            ...(r.fileId.type === "background_image" ? { background: r } : {}),
          };
          Store.commit(MUTATION_TYPES.SET_USER, updateUser);
          user.value = updateUser;
          cb && cb();
        }
      } finally {
        fileLoading.value = false;
      }
    };

    const save = async (values: FormValues) => {
      const {
        screenName,
        username,
        localizationCountryCode,
        localizationStateCode,
        localizationCity,
        localizationLanguage,
        ...questionaryInfoFields
      } = values;
      const userData: InputUserUpdate = {
        id: props.userId,
        screenName,
        username,
      };

      const userPublicInformation: InputUserPublicInformationUpdate = {
        userId: props.userId,
        location: {
          countryCode: localizationCountryCode || "",
          stateCode: localizationStateCode || "",
          city: localizationCity || "",
        },
        languages: localizationLanguage,
      };

      getQuestionaryInfoFields().forEach(field => {
        if (!questionaryInfoFields[field.name]) return;

        const trimmedValue = questionaryInfoFields[field.name].trim();
        if (trimmedValue && field.fieldMeta?.regExp) {
          if (!field.fieldMeta.appendText) {
            const execResult: RegExpExecArray = field.fieldMeta.regExp.exec(trimmedValue);
            if (execResult[1]) questionaryInfoFields[field.name] = execResult[1];
          } else if (!field.fieldMeta.regExp.test(trimmedValue)) {
            questionaryInfoFields[field.name] = `${field.fieldMeta.appendText}${trimmedValue}`;
          }
          return;
        }
        questionaryInfoFields[field.name] = trimmedValue;
      });
      const questionaryInfo: QuestionaryInfo = {
        fields: {
          ...questionaryInfoFields,
          userId: props.userId,
        },
      };
      const promises: Promise<any>[] = [
        providers.QuestionaryInfoProvider.update({ data: questionaryInfo }),
        providers.UserProvider.update({ data: userData }).then(
          r => isAuthor.value && Store.commit(MUTATION_TYPES.SET_USER, r)
        ),
      ];
      if (!config.featureHideLocalization) {
        promises.push(providers.UserPublicDataProvider.userPublicInformationUpdate(userPublicInformation));
      }
      try {
        loading.value = true;
        await Promise.all(promises);
      } finally {
        loading.value = false;
      }
      root.$router.push(getProfileUrl(props.userId));
    };

    getData();

    return { fetch, loading, user, info, files, selectedTags, fileLoading, updateUserTags, upload, save };
  },
});
